import React from 'react';
import {
  Box,
  TextField,
  Grid,
  Typography,
  FormControl,
  InputAdornment,
  FormHelperText,
  MenuItem,
} from '@mui/material';

export interface FormConfig {
  title: string;
  startMonthField: keyof ConfigState;
  startDayField: keyof ConfigState;
  baseTempField?: keyof ConfigState;
  endMonthField: keyof ConfigState;
  endDayField: keyof ConfigState;
}

export interface ConfigState {
  // Configuración Serie Horas Día
  coldHoursBaseTemp?: number;
  coldHoursStartMonth?: number;
  coldHoursStartDay?: number;
  coldHoursEndMonth?: number;
  coldHoursEndDay?: number;
  // Configuración Grados Acumulados
  accumulatedDegreesBaseTemp?: number;
  accumulatedDegreesStartMonth?: number;
  accumulatedDegreesStartDay?: number;
  accumulatedDegreesEndMonth?: number;
  accumulatedDegreesEndDay?: number;
  // Configuración Precipitaciones
  rainfallStartMonth?: number;
  rainfallStartDay?: number;
  rainfallEndMonth?: number;
  rainfallEndDay?: number;
  // Configuración Porciones de Frío (modelo Fishman, sin temperatura base)
  chillPortionsStartMonth?: number;
  chillPortionsStartDay?: number;
  chillPortionsEndMonth?: number;
  chillPortionsEndDay?: number;
}

export type ConfigStateField = keyof ConfigState;

interface DayMonthBaseFormProps {
  title: string;
  startMonthField: keyof ConfigState;
  startDayField: keyof ConfigState;
  baseTempField?: keyof ConfigState;
  endMonthField: keyof ConfigState;
  endDayField: keyof ConfigState;
  config: ConfigState;
  onConfigChange: (field: keyof ConfigState, value: string | number) => void;
  errors?: {
    [key: string]: string;
  };
}

export const DEFAULT_FORM_CONFIG = {
  coldHoursBaseTemp: 10,
  accumulatedDegreesBaseTemp: 10,
  coldHoursStartMonth: 4,
  coldHoursStartDay: 1,
  coldHoursEndMonth: 9,
  coldHoursEndDay: 30,
  accumulatedDegreesStartMonth: 4,
  accumulatedDegreesStartDay: 1,
  accumulatedDegreesEndMonth: 9,
  accumulatedDegreesEndDay: 30,
};

export const MONTHS = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' },
];

export const getDaysInMonth = (month: number): number => {
  const daysInMonth: { [key: number]: number } = {
    1: 31,
    2: 29,
    3: 31,
    4: 30,
    5: 31,
    6: 30,
    7: 31,
    8: 31,
    9: 30,
    10: 31,
    11: 30,
    12: 31,
  };
  return daysInMonth[month] || 31;
};

const DayMonthBaseForm: React.FC<DayMonthBaseFormProps> = React.memo(({
  title,
  startMonthField,
  startDayField,
  baseTempField,
  endMonthField,
  endDayField,
  config,
  onConfigChange,
  errors = {},
}) => {
  const handleMonthChange = (field: keyof ConfigState, value: string) => {
    const numValue = Number(value);
    if (!Number.isNaN(numValue) && numValue >= 1 && numValue <= 12) {
      onConfigChange(field, numValue);
    }
  };

  const handleDayChange = (field: keyof ConfigState,
    value: string, monthField: keyof ConfigState) => {
    const numValue = Number(value);
    const month = Number(config[monthField]) || 1;
    const maxDays = getDaysInMonth(month);

    if (!Number.isNaN(numValue) && numValue >= 1 && numValue <= maxDays) {
      onConfigChange(field, numValue);
    }
  };

  const handleTempChange = (field: keyof ConfigState, value: string) => {
    onConfigChange(field, value);
  };

  const hasBaseTemp = baseTempField !== undefined;
  const startMonth = Number(config[startMonthField]) || 1;
  const endMonth = Number(config[endMonthField]) || 1;
  const startDay = Number(config[startDayField]) || 1;
  const endDay = Number(config[endDayField]) || 1;

  const startMaxDays = getDaysInMonth(startMonth);
  const endMaxDays = getDaysInMonth(endMonth);

  const generateDayOptions = (maxDays: number) => {
    const days = [];
    for (let i = 1; i <= maxDays; i += 1) {
      days.push({ value: i, label: i.toString() });
    }
    return days;
  };

  return (
    <Box
      sx={{
        background: '#fff',
        borderRadius: 2,
        p: 4,
        boxShadow: 1,
        mb: 2,
        maxWidth: '100%',
      }}
    >
      <Typography variant="h5" mb={2} fontWeight="bold">
        {title}
      </Typography>
      <Grid container spacing={4}>
        <Grid item xs={12} md={hasBaseTemp ? 3 : 4}>
          <FormControl fullWidth error={!!errors[startMonthField]}>
            <TextField
              select
              label="Mes inicio"
              value={startMonth}
              onChange={(e) => handleMonthChange(startMonthField, e.target.value)}
              error={!!errors[startMonthField]}
              fullWidth
              variant="standard"
            >
              {MONTHS.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </TextField>
            {errors[startMonthField] && (
              <FormHelperText error>{errors[startMonthField]}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12} md={hasBaseTemp ? 3 : 4}>
          <FormControl fullWidth error={!!errors[startDayField]}>
            <TextField
              select
              label="Día inicio"
              value={startDay}
              onChange={(e) => handleDayChange(startDayField, e.target.value, startMonthField)}
              error={!!errors[startDayField]}
              fullWidth
              variant="standard"
            >
              {generateDayOptions(startMaxDays).map((day) => (
                <MenuItem key={day.value} value={day.value}>
                  {day.label}
                </MenuItem>
              ))}
            </TextField>
            {errors[startDayField] && (
              <FormHelperText error>{errors[startDayField]}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        {hasBaseTemp && baseTempField && (
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors[baseTempField]}>
              <TextField
                type="number"
                inputProps={{ step: 'any' }}
                label="Temperatura base"
                value={String(config[baseTempField] ?? '')}
                onChange={(e) => handleTempChange(baseTempField, e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">°C</InputAdornment>,
                }}
                error={!!errors[baseTempField]}
                helperText={errors[baseTempField]}
                fullWidth
                variant="standard"
              />
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} md={hasBaseTemp ? 3 : 4}>
          <FormControl fullWidth error={!!errors[endMonthField]}>
            <TextField
              select
              label="Mes final"
              value={endMonth}
              onChange={(e) => handleMonthChange(endMonthField, e.target.value)}
              error={!!errors[endMonthField]}
              fullWidth
              variant="standard"
            >
              {MONTHS.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </TextField>
            {errors[endMonthField] && (
              <FormHelperText error>{errors[endMonthField]}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12} md={hasBaseTemp ? 3 : 4}>
          <FormControl fullWidth error={!!errors[endDayField]}>
            <TextField
              select
              label="Día final"
              value={endDay}
              onChange={(e) => handleDayChange(endDayField, e.target.value, endMonthField)}
              error={!!errors[endDayField]}
              fullWidth
              variant="standard"
            >
              {generateDayOptions(endMaxDays).map((day) => (
                <MenuItem key={day.value} value={day.value}>
                  {day.label}
                </MenuItem>
              ))}
            </TextField>
            {errors[endDayField] && (
              <FormHelperText error>{errors[endDayField]}</FormHelperText>
            )}
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
});

DayMonthBaseForm.displayName = 'DayMonthBaseForm';

export default DayMonthBaseForm;
